import React, { useEffect, useRef, useState } from 'react'
import { Film, X, CheckCircle, AlertCircle, GripHorizontal, ImageIcon, Download } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useVideoJobStore } from '../stores/videoJobStore'

/**
 * ドラッグ移動可能なトースト：動画生成中 / 完了 / 失敗の通知
 * AI動画生成ジョブ（VideoJob）と Remotionレンダー完了通知（RenderToast）を統合表示
 */
export const VideoJobToast: React.FC = () => {
  const navigate = useNavigate()
  const { jobs, toasts, dismissToast, renderToasts, dismissRenderToast, imageGeneratingTasks, activeRenderJobs } = useVideoJobStore()
  const activeRenderEntries = Object.values(activeRenderJobs)

  const activeJobs = Object.values(jobs).filter(
    j => j.status === 'pending' || j.status === 'processing'
  )

  // ドラッグ位置（null = デフォルト位置）
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null)
  const isDragging = useRef(false)
  const dragOffset = useRef({ x: 0, y: 0 })
  const containerRef = useRef<HTMLDivElement>(null)

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!containerRef.current) return
    isDragging.current = true
    const rect = containerRef.current.getBoundingClientRect()
    const currentX = pos !== null ? pos.x : rect.left
    const currentY = pos !== null ? pos.y : rect.top
    dragOffset.current = {
      x: e.clientX - currentX,
      y: e.clientY - currentY,
    }
    if (pos === null) setPos({ x: rect.left, y: rect.top })
    e.preventDefault()
  }

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging.current) return
      const W = 288 // w-72
      setPos({
        x: Math.max(0, Math.min(window.innerWidth - W - 8, e.clientX - dragOffset.current.x)),
        y: Math.max(0, Math.min(window.innerHeight - 80, e.clientY - dragOffset.current.y)),
      })
    }
    const handleMouseUp = () => { isDragging.current = false }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [])

  // AI動画完了トーストを6秒後に自動削除
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map())
  useEffect(() => {
    const currentIds = new Set(toasts.map(t => t.id))
    for (const [id, timer] of timersRef.current) {
      if (!currentIds.has(id)) {
        clearTimeout(timer)
        timersRef.current.delete(id)
      }
    }
    toasts.forEach(t => {
      if (t.status === 'completed' && !timersRef.current.has(t.id)) {
        const timer = setTimeout(() => dismissToast(t.id), 6000)
        timersRef.current.set(t.id, timer)
      }
    })
  }, [toasts, dismissToast])

  // Remotionレンダー完了トーストを8秒後に自動削除
  const renderTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map())
  useEffect(() => {
    const currentIds = new Set(renderToasts.map(t => t.id))
    for (const [id, timer] of renderTimersRef.current) {
      if (!currentIds.has(id)) {
        clearTimeout(timer)
        renderTimersRef.current.delete(id)
      }
    }
    renderToasts.forEach(t => {
      if (!renderTimersRef.current.has(t.id)) {
        const timer = setTimeout(() => dismissRenderToast(t.id), 8000)
        renderTimersRef.current.set(t.id, timer)
      }
    })
  }, [renderToasts, dismissRenderToast])

  if (activeJobs.length === 0 && toasts.length === 0 && renderToasts.length === 0 && imageGeneratingTasks.length === 0 && activeRenderEntries.length === 0) return null

  const positionStyle: React.CSSProperties = pos !== null
    ? { position: 'fixed', top: pos.y, left: pos.x, right: 'auto' }
    : { position: 'fixed', top: 80, right: 16 }

  return (
    <div
      ref={containerRef}
      className="z-110 flex flex-col gap-2 w-72"
      style={positionStyle}
    >
      {/* ドラッグハンドル */}
      <div
        onMouseDown={handleMouseDown}
        className="flex items-center justify-center py-0.5 cursor-grab active:cursor-grabbing text-ink-muted hover:text-ink select-none"
        title="ドラッグして移動"
      >
        <GripHorizontal className="w-4 h-4" />
      </div>

      {/* 画像生成中タスク */}
      {imageGeneratingTasks.map(task => (
        <div
          key={task.taskId}
          className="flex items-center gap-3 px-4 py-3 bg-surface-2 backdrop-blur-xs border border-primary rounded-xl shadow-xl shadow-black/40"
        >
          <div className="shrink-0 w-8 h-8 rounded-full bg-primary-subtle flex items-center justify-center">
            <ImageIcon className="w-4 h-4 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-white">{task.label}</p>
            <p className="text-[10px] text-ink-muted mt-0.5">
              完了まで数分かかります
            </p>
          </div>
          <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin shrink-0" />
        </div>
      ))}

      {/* レンダリング中ジョブ（Remotion動画） */}
      {activeRenderEntries.map(entry => {
        const status = entry.status ?? 'processing'
        const progress = Math.max(0, Math.min(100, Math.round(entry.progress ?? 0)))
        const labelMain = status === 'queued'
          ? `キュー待機中${entry.queuePosition ? `（${entry.queuePosition}番目）` : ''}`
          : status === 'completed'
            ? '動画レンダリング完了'
            : `動画レンダリング中 ${progress}%`
        return (
          <div
            key={entry.jobId}
            className="flex items-start gap-3 px-4 py-3 bg-surface-2 backdrop-blur-xs border border-primary rounded-xl shadow-xl shadow-black/40"
          >
            <div className="shrink-0 w-8 h-8 rounded-full bg-primary-subtle flex items-center justify-center">
              <Film className="w-4 h-4 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-white">{labelMain}</p>
              <p className="text-[10px] text-ink-muted mt-0.5 truncate">{entry.projectName}</p>
              {/* 進捗バー */}
              <div className="mt-1.5 h-1 bg-surface-2 rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary-hover transition-all duration-300 rounded-full"
                  style={{ width: `${status === 'queued' ? 5 : progress}%` }}
                />
              </div>
            </div>
            <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin shrink-0 mt-1" />
          </div>
        )
      })}

      {/* 生成中ジョブ（AI動画） */}
      {activeJobs.map(job => (
        <div
          key={job.jobId}
          className="flex items-center gap-3 px-4 py-3 bg-surface-2 backdrop-blur-xs border border-blue-500/40 rounded-xl shadow-xl shadow-black/40"
        >
          <div className="shrink-0 w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center">
            <Film className="w-4 h-4 text-blue-400" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-white">動画生成中...</p>
            <p className="text-[10px] text-ink-muted mt-0.5">
              完了まで数分かかります
            </p>
          </div>
          <div className="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin shrink-0" />
        </div>
      ))}

      {/* 結果トースト（AI動画 完了・失敗） */}
      {toasts.map(toast => (
        <div
          key={toast.id}
          className={`flex items-center gap-3 px-4 py-3 bg-surface-2 backdrop-blur-xs rounded-xl shadow-xl shadow-black/40 border ${
            toast.status === 'completed'
              ? 'border-ok'
              : 'border-bad'
          }`}
        >
          <div className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
            toast.status === 'completed' ? 'bg-ok-bg' : 'bg-bad-bg'
          }`}>
            {toast.status === 'completed'
              ? <CheckCircle className="w-4 h-4 text-ok" />
              : <AlertCircle className="w-4 h-4 text-bad" />
            }
          </div>
          <div className="flex-1 min-w-0">
            <p className={`text-xs font-semibold ${
              toast.status === 'completed' ? 'text-ok' : 'text-bad'
            }`}>
              {toast.message}
            </p>
            {toast.status === 'completed' && (
              <p className="text-[10px] text-ink-muted mt-0.5">
                素材ページで確認できます
              </p>
            )}
          </div>
          <button
            onClick={() => dismissToast(toast.id)}
            className="shrink-0 text-ink-muted hover:text-ink transition-colors"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      ))}

      {/* Remotion レンダー完了トースト */}
      {renderToasts.map(toast => (
        <button
          key={toast.id}
          onClick={() => {
            dismissRenderToast(toast.id)
            navigate(`/project/${toast.projectId}/template`)
          }}
          className={`w-full text-left flex items-center gap-3 px-4 py-3 bg-surface-2 backdrop-blur-xs rounded-xl shadow-xl shadow-black/40 border transition-opacity hover:opacity-90 ${
            toast.status === 'completed'
              ? 'border-primary'
              : 'border-bad'
          }`}
        >
          <div className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
            toast.status === 'completed' ? 'bg-primary-subtle' : 'bg-bad-bg'
          }`}>
            {toast.status === 'completed'
              ? <CheckCircle className="w-4 h-4 text-primary" />
              : <AlertCircle className="w-4 h-4 text-bad" />
            }
          </div>
          <div className="flex-1 min-w-0">
            <p className={`text-xs font-semibold ${
              toast.status === 'completed' ? 'text-primary' : 'text-bad'
            }`}>
              {toast.status === 'completed' ? '動画レンダリング完了！' : 'レンダリングに失敗しました'}
            </p>
            <p className="text-[10px] text-ink-muted mt-0.5 truncate">
              {toast.status === 'completed'
                ? `${toast.projectName} — タップして確認`
                : toast.projectName
              }
            </p>
          </div>
          {toast.status === 'completed' && toast.downloadUrl && (
            <a
              href={toast.downloadUrl}
              download
              onClick={(e) => e.stopPropagation()}
              className="shrink-0 text-primary hover:text-sky-200 transition-colors"
              title="動画をダウンロード"
            >
              <Download className="w-4 h-4" />
            </a>
          )}
          <button
            onClick={(e) => { e.stopPropagation(); dismissRenderToast(toast.id) }}
            className="shrink-0 text-ink-muted hover:text-ink transition-colors"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </button>
      ))}
    </div>
  )
}
